// ─────────────────────────────────────────────
//  Task directives — pure logic for the board→phone one-way lane.
//  No Firestore, no React: just data shaping so it is unit-testable.
//
//  A directive is an instruction the board sends to a provider's phone. The
//  provider taps "Got it" and the board sees who has and has not seen it.
//
//  ⚠️ CONFIRM MEANS "I AM AWARE", NOT "I DID IT". Every string this module
//  produces or implies is about AWARENESS. A directive that reads as completed
//  work is a lie the board will act on — it would say a room is covered because
//  someone tapped a button while walking the other way. "Got it" / "seen", and
//  never "done" / "completed".
//
//  ⚠️ THIS IS NOT AN ALARM SURFACE. Routine room churn (a reassignment, a next
//  room) must never reach the red/blink layer the emergency system owns. Alarm
//  colour is finite: spend it on a reassignment and staff learn to ignore the
//  colour that means someone is dying. Nothing here returns a severity.
// ─────────────────────────────────────────────
import { formatClock12 } from "./stampTime";

export const TASK_KINDS = {
  MANUAL: "manual",
  AUTO: "auto",
};

// Why an automatic directive fired. Each maps to a real board event — see the
// table in docs/superpowers/specs/2026-08-15-task-directives-design.md.
export const AUTO_REASONS = {
  NEXT_ROOM: "next_room",
  RELIEVE: "relieve",
  TAKEOVER: "takeover",
  NEW_ROOM: "new_room",
  HOME: "home",
  TRANSFER_FACILITY: "transfer_facility",
  EMERGENCY_ACK: "emergency_ack",
  // A provider stamped a case phase. Owner, 2026-09-07: "when you click it save
  // the time and tell the supervisor the time, and send an alarm or a beep."
  PHASE_STAMP: "phase_stamp",
  // Somebody was named to cover a break. Until this existed, naming a cover
  // wrote the room doc and told NOBODY: there is no Firestore trigger on
  // `rooms`, so the coverer's phone stayed silent and the AIC had to ring them.
  BREAK_COVER: "break_cover",
};

// The longest instruction the input accepts. Long enough for a real sentence,
// short enough that the phone card never becomes a wall of text someone
// scrolls past during a case.
export const MAX_TASK_TEXT = 280;

// ── STATUS (DERIVED, NEVER STORED) ───────────
//
// ⚠️ THERE IS NO `status` FIELD AND THERE MUST NOT BE ONE. A stored status is a
// second source of truth that goes stale the first time a write half-fails, and
// this feature's entire job is to be trusted about who saw what. It is computed
// from the two facts that ARE stored: who it went to, and who confirmed.

// `confirmations` is a MAP keyed by uid, not an array of objects.
// Two reasons, both load-bearing:
//   1. A serverTimestamp cannot live inside an array element, so an array would
//      force the phone's own clock to stamp "seen" — unverifiable, and wrong
//      whenever a device's time is off.
//   2. Rules can constrain a map ("you may add exactly your own key") and
//      cannot meaningfully constrain arrayUnion of objects.
export function confirmedUids(task) {
  const c = task?.confirmations;
  if (!c || typeof c !== "object") return [];
  return Object.keys(c).filter((uid) => uid && c[uid]);
}

export function isConfirmedBy(task, uid) {
  return Boolean(uid) && confirmedUids(task).includes(uid);
}

export function recipientsOf(task) {
  return Array.isArray(task?.recipientIds) ? task.recipientIds.filter(Boolean) : [];
}

// ── DECLINES ─────────────────────────────────
//
// ⚠️ SILENCE USED TO MEAN FOUR THINGS. "Got it" was the only reply a phone
// could make, so a directive landing during an induction, an emergence, a prone
// flip or an open abdomen could only be IGNORED — and an unanswered directive
// reads identically whether the clinician has not seen it, has no signal, is
// scrubbed in, or is refusing. The board had to guess at which. A decline
// carries a reason, so it means exactly one thing and means it at once.
//
// Shaped as `{ [uid]: { at, reason } }` — a sibling of `confirmations`, and a
// map for the same two load-bearing reasons: a serverTimestamp cannot live
// inside an array element, and rules can say "you may add exactly your own key"
// about a map and cannot about arrayUnion.
export function declinedUids(task) {
  const d = task?.declines;
  if (!d || typeof d !== "object") return [];
  return Object.keys(d).filter((uid) => uid && d[uid] && d[uid].at);
}

/** The refusal and its reason, or null. */
export function declineOf(task, uid) {
  const entry = uid && task?.declines ? task.declines[uid] : null;
  if (!entry || !entry.at) return null;
  return { at: entry.at, reason: String(entry.reason || "") };
}

// Firestore hands a stamp back as a Timestamp, a Date, or (for one frame on the
// local echo of a write) null. Null sorts LAST here deliberately: it is the
// answer this client just gave, and treating it as the newest is what keeps a
// fresh decline from flickering back to "confirmed" before the server replies.
function stampMs(v) {
  if (!v) return Number.POSITIVE_INFINITY;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return Number.POSITIVE_INFINITY;
}

// ⚠️ THE LATEST ANSWER WINS, AND BOTH ARE POSSIBLE. A clinician who tapped
// "Got it" and was then pulled into a crash section must be able to say so, and
// the rules deliberately allow a decline after a confirm. Reading only
// `confirmations` would leave the board believing a room is covered by somebody
// who has since said they cannot come — which is the precise failure the whole
// break redesign exists to remove.
//
// @returns {"confirmed"|"declined"|null}
export function answerOf(task, uid) {
  if (!uid) return null;
  const yes = task?.confirmations ? task.confirmations[uid] : null;
  const no = declineOf(task, uid);
  if (!yes && !no) return null;
  if (yes && !no) return "confirmed";
  if (no && !yes) return "declined";
  return stampMs(no.at) >= stampMs(yes) ? "declined" : "confirmed";
}

export function answeredUids(task) {
  return recipientsOf(task).filter((uid) => answerOf(task, uid) !== null);
}

// Recipients who have not answered yet. Empty for information rows — a row
// nobody was asked to confirm can never be "pending", and counting it as such
// would put a permanent unclearable number on the board.
//
// ⚠️ A DECLINE IS AN ANSWER. Somebody who has refused is no longer PENDING —
// they are not a person the board is still waiting on, they are a person the
// board must now act on. Leaving them in this list would keep the "waiting"
// counter ticking against a question that has already been answered, and the
// unanswered-directive escalation would nag forever about a refusal everyone
// has already seen.
export function pendingUids(task) {
  if (!requiresConfirm(task)) return [];
  const done = new Set(answeredUids(task));
  return recipientsOf(task).filter((uid) => !done.has(uid));
}

// `requiresConfirm: false` marks a row as pure information (emergency_ack).
// Defaults to TRUE when absent: a directive whose flag failed to write is
// treated as needing an answer, which fails toward someone being asked twice
// rather than toward an instruction nobody was ever asked to see.
export function requiresConfirm(task) {
  return task?.requiresConfirm !== false;
}

// "info" — nothing to confirm.
// "confirmed" — every recipient has seen it.
// "pending" — at least one has not.
export function taskStatus(task) {
  if (!requiresConfirm(task)) return "info";
  const total = recipientsOf(task).length;
  if (total === 0) return "info";
  // ⚠️ A REFUSAL OUTRANKS BOTH. "declined" is the only one of these three that
  // needs somebody to DO something — find another cover — so it must not be
  // hidden behind a "pending" that merely means "still waiting", nor behind a
  // "confirmed" earned by the other recipients.
  if (declinedUids(task).some((uid) => answerOf(task, uid) === "declined")) return "declined";
  return pendingUids(task).length === 0 ? "confirmed" : "pending";
}

// ── SORTING ──────────────────────────────────
// Newest first, on both surfaces.
//
// ⚠️ A DOC WITH NO `createdAt` SORTS FIRST, NOT LAST. Firestore's
// serverTimestamp is null on the local echo of a write for one frame before the
// server value arrives. That doc is the one the user just created, so treating
// a missing stamp as "now" keeps it at the top instead of making it flash in at
// the bottom of a long list and jump.
function createdMs(task) {
  const t = task?.createdAt;
  if (!t) return Number.POSITIVE_INFINITY;
  if (typeof t.toMillis === "function") return t.toMillis();
  if (t instanceof Date) return t.getTime();
  if (typeof t === "number") return t;
  if (typeof t.seconds === "number") return t.seconds * 1000;
  return Number.POSITIVE_INFINITY;
}

// ⚠️ A SIGN COMPARATOR, NEVER A SUBTRACTION. `Infinity - Infinity` is NaN, and
// an inconsistent comparator does not merely tie the two rows involved — V8's
// sort is free to misorder elements that have nothing to do with them.
//
// This is not a theoretical case here, it is the ordinary one: a handover
// writes TWO documents back to back (the recipient's and the counterpart's),
// and both local echoes carry `createdAt: null` for a frame, which the rule
// above maps to +Infinity. So every handover send put two Infinity rows in one
// snapshot, on both surfaces. domain/roomMessageBox already carries this exact
// diagnosis — "the classic way a 'does not crash' sort still scrambles a list"
// — and returns -1/0/1; this module kept the subtraction.
export function sortTasksNewestFirst(tasks) {
  return [...(tasks || [])].filter(Boolean).sort((a, b) => {
    const at = createdMs(a);
    const bt = createdMs(b);
    if (at === bt) return 0;
    return bt > at ? 1 : -1;
  });
}

// ── PHONE VIEW ───────────────────────────────
// What one provider sees. `identity` is the set of ids that mean "me" — a user
// can be known by their auth uid and by a staff id, and a directive addressed
// to either must reach them.
export function tasksForRecipient(tasks, identity) {
  const ids = identity instanceof Set ? identity : new Set([identity].filter(Boolean));
  if (!ids.size) return [];
  return sortTasksNewestFirst(
    (tasks || []).filter((t) => recipientsOf(t).some((uid) => ids.has(uid)))
  );
}

// ── BOARD VIEW ───────────────────────────────
// One row per recipient: who it went to, whether they have seen it, when.
// `nameByUid` is display only — an unresolved uid still shows as a row, because
// silently dropping a recipient the board cannot name would under-report who
// was asked.
export function confirmationLedger(task, nameByUid = {}) {
  const confirmations = task?.confirmations || {};
  return recipientsOf(task).map((uid) => {
    // ⚠️ THE ANSWER, NOT MERELY THE CONFIRMATION. Reading `confirmations` alone
    // renders a ✓ beside somebody who has since refused, so the one surface an
    // AIC watches all day would show a room as answered by a person who has
    // said they cannot come.
    const answer = answerOf(task, uid);
    const declined = answer === "declined";
    const refusal = declined ? declineOf(task, uid) : null;
    return {
      uid,
      name: nameByUid[uid] || "Unknown",
      at: declined ? refusal.at : confirmations[uid] || null,
      seen: answer === "confirmed",
      declined,
      // Carried through because "declined" on its own is barely better than
      // silence: the board acts differently on "open abdomen" than "off shift".
      reason: refusal ? refusal.reason : "",
    };
  });
}

// "1 of 3 seen". Callers render the count; this decides what the count means.
//
// ⚠️ AN INFORMATION ROW COUNTS NOTHING — all three numbers are zero.
// It used to report `{ seen: 0, total: 1, pending: 1 }` for an emergency_ack
// receipt: nobody was ever asked to confirm it, so that `pending: 1` was a
// number on the board that no action could ever clear. `pendingUids` and
// `taskStatus` already refused to count it; this function did not, and the only
// thing standing between that and the board was every future caller remembering
// to check the flag first. It checks the flag itself now.
//
// A caller that wants "how many people were told" wants `recipientsOf`. This
// summary is strictly about the ASK.
export function seenSummary(task) {
  if (!requiresConfirm(task)) return { seen: 0, total: 0, pending: 0, declined: 0 };
  const recipients = recipientsOf(task);
  const seen = recipients.filter((uid) => answerOf(task, uid) === "confirmed").length;
  // ⚠️ A REFUSAL IS NOT PENDING. Counting it as still-waiting leaves a number
  // on the board that no amount of waiting can clear, which is the same defect
  // the information-row note above records and fixes.
  const declined = recipients.filter((uid) => answerOf(task, uid) === "declined").length;
  return {
    seen,
    total: recipients.length,
    pending: Math.max(0, recipients.length - seen - declined),
    declined,
  };
}

// Every recipient across today's directives who still owes an answer, deduped.
// This is what the board's unconfirmed line counts.
export function outstandingRecipients(tasks) {
  const out = new Set();
  (tasks || []).forEach((t) => pendingUids(t).forEach((uid) => out.add(uid)));
  return Array.from(out);
}

// ── WRITE PAYLOAD ────────────────────────────
// Validation at the boundary. Returns null when the directive is not sendable,
// so a caller cannot write an empty instruction or one addressed to nobody.
//
// ⚠️ `recipientIds` MUST BE AUTH UIDS. Staff ids look identical and fail
// silently: the doc writes, the board shows it sent, and no phone ever rings.
// Callers resolve staff -> uid BEFORE calling this, the same conversion
// AlertContext already performs for alert targets.
export function buildManualTask({
  facilityId,
  dateKey,
  senderId,
  senderName = "",
  recipientIds = [],
  text = "",
  roomName = null,
} = {}) {
  const body = String(text || "").trim();
  const to = Array.from(new Set((recipientIds || []).filter(Boolean)));
  if (!facilityId || !dateKey || !senderId) return null;
  if (!body || !to.length) return null;
  return {
    facilityId,
    dateKey,
    senderId,
    senderName: String(senderName || "").trim(),
    recipientIds: to,
    text: body.slice(0, MAX_TASK_TEXT),
    kind: TASK_KINDS.MANUAL,
    autoReason: null,
    roomName: roomName || null,
    requiresConfirm: true,
  };
}

// Whether the Send control may fire. Kept separate from buildManualTask so the
// button can disable without constructing a doc on every keystroke.
export function canSend({ text = "", recipientIds = [] } = {}) {
  return Boolean(String(text || "").trim()) && (recipientIds || []).filter(Boolean).length > 0;
}

// ── PHASE DIRECTIVE ──────────────────────────
// The supervisor's copy of a phase stamp.
//
// ⚠️ THIS IS A NOTIFICATION, NOT A SECOND ACKNOWLEDGEMENT. `requiresConfirm` is
// FALSE on purpose: the acknowledgement of record for a phase is the Ack button
// in the Room Status grid, which writes onto `or_cases.phases[id].ack`. Setting
// it true here would put two different "I saw it" controls on one event, in two
// places, writing to two documents — and then neither answers "has the
// supervisor seen the induction?" on its own.
//
// ⚠️ AND IT IS NOT AN ALARM. It rides the ordinary task-push path, which
// deliberately avoids the emergency channel, high priority and time-sensitive
// interruption. Induction and emergence are routine moments of every case;
// spending alarm loudness on them is how staff learn to ignore the loudness
// that means somebody is dying. The phone beeps, it does not scream.
//
// ⚠️ `recipientIds` MUST BE AUTH UIDS, exactly as buildManualTask says. A staff
// id writes cleanly and no phone ever rings.
export function buildPhaseTask({
  facilityId,
  dateKey,
  senderId,
  senderName = "",
  recipientIds = [],
  roomName = null,
  phaseLabel = "",
  at = null,
} = {}) {
  const to = Array.from(new Set((recipientIds || []).filter(Boolean)));
  const label = String(phaseLabel || "").trim();
  if (!facilityId || !dateKey || !senderId) return null;
  if (!label || !to.length) return null;

  // The TIME is the payload — that is what the owner asked to be told, and a
  // directive that only said "Induction" would make the supervisor go and look
  // it up. Falls back to naming the phase alone rather than inventing an hour.
  const when = formatStampTime(at);
  const where = roomName ? `${roomName} · ` : "";
  const who = String(senderName || "").trim();
  const text = when
    ? `${where}${label} at ${when}${who ? ` — ${who}` : ""}`
    : `${where}${label}${who ? ` — ${who}` : ""}`;

  return {
    facilityId,
    dateKey,
    senderId,
    senderName: who,
    recipientIds: to,
    text: text.slice(0, MAX_TASK_TEXT),
    kind: TASK_KINDS.AUTO,
    autoReason: AUTO_REASONS.PHASE_STAMP,
    roomName: roomName || null,
    requiresConfirm: false,
  };
}

// The 12-hour spelling, for the PROSE of a directive — "at 9:41 PM" is how a
// notification reads and "at 21:41" is not.
//
// ⚠️ THE COERCION IS NOT WRITTEN HERE. This used to do `new Date(value)`, which
// is Invalid Date for a Firestore Timestamp — the shape a live listener actually
// hands back — so it silently produced "" for every real stamp. domain/stampTime
// owns reading a recorded time, and both the phone and the board now import it.
// ⚠️ IMPORT **AND** RE-EXPORT. `export { x } from "./y"` alone creates NO local
// binding, so `buildPhaseTask` above — which calls formatStampTime in this same
// module — threw "formatStampTime is not defined" at runtime while the build
// stayed green. The same trap `useControlPanelData` documents for benchProviders.
export const formatStampTime = formatClock12;
